import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../Components/Sidebar";
import PageComponent from "../../Components/PageComponent";
import { MdOutlineDeleteForever } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import {
  addIntake,
  getIntakes,
  updateIntake,
  deleteIntake,
} from "../../controllers/aluminiIntakes.controller";
import DeletePopup from "../../Components/DeletePopup";
import UpdateIntakePopup from "./UpdateIntakePopup";
import { fetchDegreeNames } from "../../redux/actions/degreeActions";

function Intakes() {
  const dispatch = useDispatch();
  const [intakes, setIntakes] = useState([]);
  const [intake, setIntake] = useState("");
  const [degree, setDegree] = useState("");
  const [editId, setEditId] = useState(null);

  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [selectedIntake, setSelectedIntake] = useState(null);

  const [isUpdatePopupOpen, setIsUpdatePopupOpen] = useState(false);
  const [intakeToUpdate, setIntakeToUpdate] = useState(null);

  const degreenames = useSelector((state) => state.degree.degrees.data) || [];

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5); // You can adjust the page size
  const [totalIntakes, setTotalIntakes] = useState(0); // total number of intakes

  useEffect(() => {
    fetchIntakes();
    dispatch(fetchDegreeNames());
  }, [currentPage, pageSize]); // Fetch when page or page size changes

  const fetchIntakes = async () => {
    try {
      const response = await getIntakes({ page: currentPage, page_size: pageSize });
      setIntakes(response.data.data);
      //setting total number of intakes
      setTotalIntakes(response.data.pagination.total);
    } catch (error) {
      console.error("Error fetching intakes:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!intake) {
      alert("Please enter an intake name.");
      return;
    }

    if (!degree) {
      alert("Please enter degree.");
      return;
    }
    try {
      await addIntake({ alumniIntake: intake, degreeProgram: degree });
      alert("Intake added successfully!");
      setIntake("");
      fetchIntakes();
    } catch (error) {
      console.error("Error saving intake:", error);
      alert("Failed to save intake.");
    }
  };

  // Open delete popup
  const handleDeleteClick = (intake) => {
    setSelectedIntake(intake);
    setIsDeletePopupOpen(true);
  };

  // Confirm deletion
  const handleConfirmDelete = async () => {
    try {
      if (selectedIntake) {
        await deleteIntake({ id: selectedIntake.id });
        alert("Intake deleted successfully!");
        fetchIntakes();
      }
    } catch (error) {
      console.error("Error deleting intake:", error);
      alert("Failed to delete intake.");
    } finally {
      setIsDeletePopupOpen(false);
      setSelectedIntake(null);
    }
  };

  const handleCloseDeletePopup = () => {
    setIsDeletePopupOpen(false);
    setSelectedIntake(null);
  };

  const handleEditClick = (intake) => {
    setIntakeToUpdate(intake);
    setIsUpdatePopupOpen(true);
  };

  const handleCloseUpdatePopup = () => {
    setIsUpdatePopupOpen(false);
    setIntakeToUpdate(null);
  };

  const handleUpdateIntake = async (id, updatedIntakeData) => {
    try {
      await updateIntake({
        id,
        alumniIntake: updatedIntakeData.alumniIntake,
        degreeProgram: updatedIntakeData.degreeProgram, // Include degreeProgram
      });
      fetchIntakes();
    } catch (error) {
      console.error("Error updating intake:", error);
      alert("Failed to update intake.");
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const totalPages = Math.ceil(totalIntakes / pageSize);

  return (
    <PageComponent>
      <div className="flex w-screen h-screen overflow-x-hidden bg-[#F8F7FA]">
        <div className="fixed top-0 left-0 w-64 h-full overflow-y-auto bg-white shadow-md">
          <Sidebar />
        </div>
        <div className="flex-1 p-6 ml-64">
          <div className="flex items-center space-x-2 text-3xl text-gray-500">
            <span>Alumni</span>
            <span>/</span>
            <span className="text-black">Intakes</span>
          </div>

          <div className="p-6 mt-6 bg-white rounded-lg shadow-md">
            <h2 className="mb-4 text-xl font-semibold text-gray-700">
              Add Intake
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    className="block mb-2 text-sm font-medium text-gray-600"
                    htmlFor="degree"
                  >
                    Degree Program
                  </label>
                  <select
                    id="degree"
                    name="degree"
                    value={degree}
                    onChange={(e) => setDegree(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                  >
                    <option value="">Select Degree</option>
                    {degreenames.map((degree) => (
                      <option key={degree.id} value={degree.id}>
                        {degree.program_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-600">
                    Intake
                  </label>
                  <input
                    type="text"
                    value={intake}
                    onChange={(e) => setIntake(e.target.value)}
                    placeholder="Enter intake"
                    className="w-3/4 px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                </div>
                <div></div>
              </div>
              <div className="flex justify-end mt-12 space-x-4">
                <button
                  type="reset"
                  onClick={() => {
                    setIntake("");
                    setDegree("");
                    setEditId(null);
                  }}
                  className="px-4 py-2 text-[#A8AAAE] bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-[#92288C] rounded-md hover:bg-[#6C0B67]"
                >
                  Add
                </button>
              </div>
            </form>
          </div>

          {/* Intake Table */}
          <div className="p-4 mt-4 bg-white border border-gray-300 rounded-lg shadow-md">
            <h2 className="mb-4 text-xl font-semibold text-gray-700">
              Added Intakes
            </h2>
            <table className="w-full border border-gray-300 table-auto">
              <thead>
                <tr className="text-left text-gray-600 bg-gray-100">
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">Intake</th>
                  <th className="px-4 py-2">Degree Name</th>
                  <th className="px-4 py-2">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {intakes.length > 0 ? (
                  intakes.map((item, index) => (
                    <tr key={item.id} className="border-b border-gray-300">
                      <td className="px-4 py-2">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="px-4 py-2">{item.alumni_intake}</td>
                      <td className="px-4 py-2">{item.program_name}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center space-x-4">
                          <button
                            type="button"
                            className="flex items-center px-5 py-1 text-gray-400 bg-gray-100 border border-gray-400 rounded"
                            onClick={() => handleDeleteClick(item)}
                          >
                            Delete
                            <MdOutlineDeleteForever className="w-5 h-5 ml-2" />
                          </button>
                          <button
                            type="button"
                            className="flex items-center px-5 py-1 border rounded text-primary-color border-primary-color bg-primary-color bg-opacity-10"
                            onClick={() => handleEditClick(item)}
                          >
                            Edit
                            <FaRegEdit className="w-4 h-4 ml-2" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="px-4 py-2 text-center text-gray-500"
                    >
                      No intakes available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-4">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 mx-1 bg-gray-200 rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            {/* Display page numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 mx-1 rounded-md ${
                    currentPage === page
                      ? "bg-[#92288C] text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 mx-1 bg-gray-200 rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        {/* Delete Confirmation Popup */}
        <DeletePopup
          title="Intake"
          isOpen={isDeletePopupOpen}
          onClose={handleCloseDeletePopup}
          onConfirm={handleConfirmDelete}
          data={selectedIntake?.alumni_intake || ""}
        />

        {/* Update Intake Popup */}
        {/* Update Intake Popup */}
        <UpdateIntakePopup
          title="Update Intake"
          isOpen={isUpdatePopupOpen}
          onClose={handleCloseUpdatePopup}
          fetchIntakes={fetchIntakes}
          updateIntake={handleUpdateIntake}
          data={intakeToUpdate}
          degreeNames={degreenames} // Pass degreeNames here
        />
      </div>
    </PageComponent>
  );
}

export default Intakes;